/* Peter Suchy Jewelers — header behaviour (vanilla, no dependencies).
   Loaded from sections/header.liquid. Everything hangs off the <header-root>
   custom element: when the theme editor re-renders or removes the header
   section (shopify:section:load / :unload), the old element's listeners are
   torn down in disconnectedCallback and the new markup sets itself up in
   connectedCallback.

   - Desktop nav: disclosure pattern — each item's caret button opens its
     panel; flyouts for the third level; hover intent for the mouse.
   - Mobile drawer: mmenu-style sliding panels, built from the desktop nav
     the first time the drawer opens.
   - Predictive search: ARIA combobox over the Section Rendering API.
   - Cart link: keeps its accessible name in step with the count badge. */
(() => {
  'use strict';

  const HOVER_OPEN_DELAY = 120;
  const HOVER_CLOSE_DELAY = 250;
  const SEARCH_DEBOUNCE = 250;

  const labelOf = (el) => el.textContent.replace(/\s+/g, ' ').trim();

  /* ---------- Desktop nav: disclosure menus ---------- */
  // A "disclosure" is a top-level item ([data-nav-item]) or a dropdown row
  // with a flyout ([data-nav-flyout]): an <li> whose direct child button
  // (aria-expanded, aria-controls) opens the panel it names.
  const toggleOf = (item) => item.querySelector(':scope > [aria-expanded]');
  const panelOf = (item) => {
    const id = toggleOf(item)?.getAttribute('aria-controls');
    return id ? item.querySelector(`#${CSS.escape(id)}`) : null;
  };

  class DesktopNav {
    constructor(nav, signal) {
      this.nav = nav;
      this.items = [...nav.querySelectorAll('[data-nav-item]')].filter(toggleOf);
      this.flyouts = [...nav.querySelectorAll('[data-nav-flyout]')].filter(toggleOf);
      this.openedBy = new Map();
      this.timers = new Map();

      [...this.items, ...this.flyouts].forEach((item) => {
        item.addEventListener('pointerenter', (event) => this.onPointerEnter(event, item), { signal });
        item.addEventListener('pointerleave', (event) => this.onPointerLeave(event, item), { signal });
      });

      nav.addEventListener('click', (event) => this.onClick(event), { signal });
      nav.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
      nav.addEventListener('focusin', (event) => this.onFocusIn(event), { signal });
      nav.addEventListener('focusout', (event) => this.onFocusOut(event), { signal });

      // Tap or click anywhere else closes, as does Escape when focus is
      // outside the nav (a menu the mouse opened).
      document.addEventListener(
        'pointerdown',
        (event) => {
          if (!nav.contains(event.target)) this.closeAll();
        },
        { signal }
      );
      document.addEventListener(
        'keydown',
        (event) => {
          if (event.key === 'Escape' && !nav.contains(event.target)) this.closeAll();
        },
        { signal }
      );

      signal.addEventListener('abort', () => this.timers.forEach((timer) => clearTimeout(timer)));
    }

    isOpen(item) {
      return item.classList.contains('is-open');
    }

    open(item, how) {
      this.clearTimer(item);
      // Only one panel per level: another top item, or a sibling flyout.
      const siblings = this.items.includes(item)
        ? this.items
        : this.flyouts.filter((flyout) => flyout.parentElement === item.parentElement);
      siblings.forEach((other) => other !== item && this.close(other));

      item.classList.add('is-open');
      toggleOf(item).setAttribute('aria-expanded', 'true');
      this.openedBy.set(item, how);
    }

    close(item) {
      this.clearTimer(item);
      if (!this.isOpen(item)) return;
      this.flyouts.forEach((flyout) => item !== flyout && item.contains(flyout) && this.close(flyout));
      item.classList.remove('is-open');
      toggleOf(item).setAttribute('aria-expanded', 'false');
      this.openedBy.delete(item);
    }

    closeAll() {
      this.items.forEach((item) => this.close(item));
    }

    clearTimer(item) {
      clearTimeout(this.timers.get(item));
      this.timers.delete(item);
    }

    // Links a panel's arrow keys step through: every link in it, less those
    // inside a nested flyout (they have their own panel).
    rows(item) {
      const panel = panelOf(item);
      if (!panel) return [];
      return [...panel.querySelectorAll('a[href]')].filter((link) => {
        const flyoutPanel = link.closest('.header__flyout');
        return !flyoutPanel || flyoutPanel === panel;
      });
    }

    focusRow(item, which) {
      const rows = this.rows(item);
      const row = which === 'last' ? rows[rows.length - 1] : rows[0];
      row?.focus();
    }

    /* Mouse hover, with a short delay each way so crossing the bar or
       cutting a corner into a flyout doesn't flicker menus open and shut.
       Touch and pen don't hover: they use the caret buttons. */
    onPointerEnter(event, item) {
      if (event.pointerType !== 'mouse') return;
      this.clearTimer(item);
      if (this.isOpen(item)) return;
      this.timers.set(item, setTimeout(() => this.open(item, 'hover'), HOVER_OPEN_DELAY));
    }

    onPointerLeave(event, item) {
      if (event.pointerType !== 'mouse') return;
      this.clearTimer(item);
      // A panel opened by click or keyboard stays until dismissed.
      if (this.openedBy.get(item) !== 'hover') return;
      this.timers.set(item, setTimeout(() => this.close(item), HOVER_CLOSE_DELAY));
    }

    onClick(event) {
      const toggle = event.target.closest('.header__menu-button, .header__flyout-toggle');
      if (!toggle) return;
      const item = toggle.parentElement;
      // detail is 0 when Enter or Space pressed the button.
      const fromKeyboard = event.detail === 0;

      if (!this.isOpen(item)) {
        this.open(item, fromKeyboard ? 'keyboard' : 'click');
        if (fromKeyboard) this.focusRow(item, 'first');
      } else if (this.openedBy.get(item) === 'hover' && !fromKeyboard) {
        // Clicking a caret the hover already opened pins it open.
        this.openedBy.set(item, 'click');
      } else {
        this.close(item);
      }
    }

    onKeydown(event) {
      const { key, target } = event;
      const item = target.closest('[data-nav-item]');
      if (!item) return;
      const hasPanel = Boolean(toggleOf(item));

      const flyout = target.closest('[data-nav-flyout]');
      const inFlyoutPanel = Boolean(flyout && panelOf(flyout)?.contains(target));
      const inTopPanel = hasPanel && Boolean(panelOf(item)?.contains(target));
      const isTopControl = target.parentElement === item;

      if (key === 'Escape') {
        // Innermost open panel first: a flyout, then its dropdown.
        const owner = flyout && this.isOpen(flyout) ? flyout : item;
        if (!hasPanel || !this.isOpen(owner)) return;
        event.preventDefault();
        this.close(owner);
        toggleOf(owner).focus();
        return;
      }

      if (isTopControl) {
        if (hasPanel && (key === 'ArrowDown' || key === 'ArrowUp')) {
          event.preventDefault();
          this.open(item, 'keyboard');
          this.focusRow(item, key === 'ArrowDown' ? 'first' : 'last');
        } else if (key === 'ArrowLeft' || key === 'ArrowRight') {
          // Step along the bar: every top-level link and caret button.
          const controls = [...this.nav.querySelectorAll('[data-nav-item] > :is(a, button)')];
          const next = controls[controls.indexOf(target) + (key === 'ArrowRight' ? 1 : -1)];
          if (next) {
            event.preventDefault();
            next.focus();
          }
        }
        return;
      }

      if (!inTopPanel) return;
      const level = inFlyoutPanel ? flyout : item;

      if (key === 'ArrowDown' || key === 'ArrowUp' || key === 'Home' || key === 'End') {
        const rows = this.rows(level);
        // A flyout's caret button counts as the row of the link beside it.
        const current = target.matches('.header__flyout-toggle') ? target.previousElementSibling : target;
        const index = rows.indexOf(current);
        let next;
        if (key === 'Home') next = rows[0];
        else if (key === 'End') next = rows[rows.length - 1];
        else next = rows[(index + (key === 'ArrowDown' ? 1 : -1) + rows.length) % rows.length];
        if (next) {
          event.preventDefault();
          next.focus();
        }
      } else if (key === 'ArrowRight' && flyout && !inFlyoutPanel) {
        event.preventDefault();
        this.open(flyout, 'keyboard');
        this.focusRow(flyout, 'first');
      } else if (key === 'ArrowLeft' && inFlyoutPanel) {
        event.preventDefault();
        this.close(flyout);
        toggleOf(flyout).focus();
      }
    }

    // Keyboard focus landing in another item closes the one that was open;
    // keyboard focus inside a hover-opened panel keeps it from closing
    // when the mouse wanders off.
    onFocusIn(event) {
      const item = event.target.closest('[data-nav-item]');
      this.items.forEach((other) => other !== item && this.close(other));
      if (!event.target.matches(':focus-visible')) return;
      [...this.items, ...this.flyouts].forEach((open) => {
        if (open.contains(event.target) && this.openedBy.get(open) === 'hover') {
          this.openedBy.set(open, 'keyboard');
        }
      });
    }

    onFocusOut(event) {
      const next = event.relatedTarget;
      // No relatedTarget: a click on a non-focusable spot — left to pointerdown.
      if (!next) return;
      if (!this.nav.contains(next)) {
        this.closeAll();
        return;
      }
      this.flyouts.forEach((flyout) => !flyout.contains(next) && this.close(flyout));
    }
  }

  /* ---------- Mobile drawer: mmenu-style sliding panels ---------- */
  class MobileMenu {
    constructor(root, signal) {
      this.drawer = root.querySelector('#MenuDrawer');
      this.mmenu = this.drawer?.querySelector('[data-mmenu]');
      this.nav = root.querySelector('.header__nav');
      if (!this.mmenu) return;
      this.openers = new Map();

      // Build the sub-menu panels as the drawer opens, before the first
      // advance, so they slide in rather than appear.
      root.querySelector('.header__menu-toggle')?.addEventListener('click', () => this.build(), { signal });
      this.mmenu.addEventListener('click', (event) => this.onClick(event), { signal });
      this.drawer.addEventListener('close', () => this.reset(), { signal });
    }

    // Returns true when this call did the building.
    build() {
      const template = this.mmenu.querySelector('template[data-mmenu-template]');
      if (!template || !this.nav) return false;

      const parts = template.content;
      const panelPart = parts.querySelector('.mmenu__panel');
      const linkPart = parts.querySelector('[data-mmenu-link]');
      const advancePart = parts.querySelector('[data-mmenu-advance]');
      const viewAll = this.mmenu.dataset.viewAll || '[title]';
      const panels = document.createDocumentFragment();

      const addPanel = (name, back, link) => {
        const title = labelOf(link);
        const panel = panelPart.cloneNode(true);
        panel.dataset.menuPanel = name;
        panel.setAttribute('aria-label', title);
        panel.querySelector('[data-menu-back]').dataset.menuBack = back;
        panel.querySelector('.mmenu__title').textContent = title;
        const all = panel.querySelector('[data-view-all]');
        all.href = link.getAttribute('href');
        all.textContent = viewAll.replace('[title]', title);
        panels.append(panel);
        return panel.querySelector('.mmenu__list');
      };

      const addLink = (list, link) => {
        const row = linkPart.cloneNode(true);
        const anchor = row.querySelector('a');
        anchor.href = link.getAttribute('href');
        anchor.textContent = labelOf(link);
        list.append(row);
      };

      const addAdvance = (list, link, target) => {
        const row = advancePart.cloneNode(true);
        const button = row.querySelector('button');
        button.dataset.menuAdvance = target;
        button.prepend(labelOf(link));
        list.append(row);
      };

      this.nav.querySelectorAll('[data-nav-item]').forEach((item) => {
        const children = item.querySelectorAll('[data-nav-child]');
        if (!children.length) return;
        const name = `panel-${item.dataset.navIndex}`;
        const list = addPanel(name, 'root', item.querySelector(':scope > .header__menu-link'));

        children.forEach((child, index) => {
          const link = child.querySelector('a');
          const grandchildren = child.querySelectorAll('[data-nav-grandchildren] a');
          if (!grandchildren.length) {
            addLink(list, link);
            return;
          }
          const childName = `${name}-${index + 1}`;
          addAdvance(list, link, childName);
          const sublist = addPanel(childName, name, link);
          grandchildren.forEach((grandchild) => addLink(sublist, grandchild));
        });
      });

      template.replaceWith(panels);
      return true;
    }

    onClick(event) {
      const trigger = event.target.closest('[data-menu-advance], [data-menu-back]');
      if (!trigger) return;
      const justBuilt = this.build();

      const isBack = trigger.hasAttribute('data-menu-back');
      const name = trigger.dataset.menuAdvance || trigger.dataset.menuBack;
      const target = this.mmenu.querySelector(`[data-menu-panel="${CSS.escape(name)}"]`);
      const current = this.mmenu.querySelector('.mmenu__panel.is-active');
      if (!target || target === current) return;
      // Panels built this instant need a style pass before sliding in.
      if (justBuilt) void target.offsetWidth;

      if (!isBack) this.openers.set(target, trigger);
      current.classList.remove('is-active');
      current.classList.toggle('is-parked', !isBack);
      target.classList.remove('is-parked');
      target.classList.add('is-active');

      // Forward: the new panel's title. Back: the row that opened the panel
      // being left, so the user lands where they were.
      const opener = isBack ? this.openers.get(current) : null;
      const focusTarget = opener && target.contains(opener) ? opener : target.querySelector('.mmenu__title');
      focusTarget?.focus({ preventScroll: true });
    }

    // Back to the first panel whenever the drawer closes.
    reset() {
      this.mmenu.querySelectorAll('.mmenu__panel').forEach((panel) => {
        panel.classList.remove('is-active', 'is-parked');
        if (panel.dataset.menuPanel === 'root') panel.classList.add('is-active');
      });
    }
  }

  /* ---------- Predictive search: ARIA combobox ---------- */
  class PredictiveSearch {
    constructor(dialog, signal) {
      this.dialog = dialog;
      this.input = dialog.querySelector('[data-predictive-input]');
      this.results = dialog.querySelector('[data-predictive-results]');
      this.listbox = dialog.querySelector('[data-predictive-listbox]');
      this.footer = dialog.querySelector('[data-predictive-footer]');
      this.status = dialog.querySelector('[data-predictive-status]');
      if (!this.input || !this.results || !this.listbox) return;

      this.options = [];
      this.active = null;
      this.escapeHandled = false;

      this.input.addEventListener('input', () => this.onInput(), { signal });
      this.input.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
      // Escape that cleared the field shouldn't also close the dialog.
      dialog.addEventListener(
        'cancel',
        (event) => {
          if (this.escapeHandled) event.preventDefault();
        },
        { signal }
      );
      dialog.addEventListener('close', () => this.setActive(null), { signal });
      signal.addEventListener('abort', () => {
        clearTimeout(this.timer);
        this.controller?.abort();
      });
    }

    onInput() {
      clearTimeout(this.timer);
      const query = this.input.value.trim();
      if (query.length < 2) {
        this.controller?.abort();
        this.clear();
        return;
      }
      this.timer = setTimeout(() => this.search(query), SEARCH_DEBOUNCE);
    }

    async search(query) {
      // Only the latest request counts: abort the one still in flight.
      this.controller?.abort();
      this.controller = new AbortController();
      const params = new URLSearchParams({
        q: query,
        'resources[type]': 'query,collection,page,product',
        'resources[limit]': '6',
        'resources[limit_scope]': 'each',
        section_id: 'predictive-search',
      });

      const controller = this.controller;
      this.results.classList.add('is-loading');
      try {
        const response = await fetch(`${this.results.dataset.url}?${params}`, {
          signal: this.controller.signal,
        });
        if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (this.input.value.trim() !== query) return;
        this.render(html);
      } catch (error) {
        if (error.name !== 'AbortError') console.error(error);
      } finally {
        if (this.controller === controller) this.results.classList.remove('is-loading');
      }
    }

    render(html) {
      this.listbox.innerHTML = html.querySelector('[data-predictive-groups]')?.innerHTML || '';
      this.footer.innerHTML = html.querySelector('[data-predictive-footer]')?.innerHTML || '';
      this.options = [...this.listbox.querySelectorAll('[role="option"]')];
      this.setActive(null);
      this.setExpanded(this.options.length > 0);
      const status = html.querySelector('[data-predictive-status]');
      this.announce(status ? labelOf(status) : '');
    }

    clear() {
      this.listbox.innerHTML = '';
      this.footer.innerHTML = '';
      this.options = [];
      this.setActive(null);
      this.setExpanded(false);
      this.announce('');
    }

    setExpanded(expanded) {
      this.listbox.hidden = !expanded;
      this.input.setAttribute('aria-expanded', String(expanded));
    }

    setActive(option) {
      this.active?.setAttribute('aria-selected', 'false');
      this.active = option;
      if (option) {
        option.setAttribute('aria-selected', 'true');
        this.input.setAttribute('aria-activedescendant', option.id);
        option.scrollIntoView({ block: 'nearest' });
      } else {
        this.input.removeAttribute('aria-activedescendant');
      }
    }

    announce(text) {
      if (this.status) this.status.textContent = text;
    }

    onKeydown(event) {
      switch (event.key) {
        case 'ArrowDown':
        case 'ArrowUp': {
          if (!this.options.length) return;
          event.preventDefault();
          this.setExpanded(true);
          const last = this.options.length - 1;
          const index = this.options.indexOf(this.active);
          let next;
          if (event.key === 'ArrowDown') next = index === -1 || index === last ? 0 : index + 1;
          else next = index <= 0 ? last : index - 1;
          this.setActive(this.options[next]);
          break;
        }
        case 'Enter':
          // With an option active, follow it; otherwise the form submits.
          if (this.active && !this.listbox.hidden) {
            event.preventDefault();
            this.active.click();
          }
          break;
        case 'Escape':
          // First Escape clears the field and results; the next closes.
          if (this.input.value || !this.listbox.hidden) {
            event.preventDefault();
            this.escapeHandled = true;
            setTimeout(() => (this.escapeHandled = false));
            clearTimeout(this.timer);
            this.controller?.abort();
            this.input.value = '';
            this.clear();
          }
          break;
        default:
      }
    }
  }

  /* ---------- Cart link name ---------- */
  // global.js rewrites the badge text when the cart changes; mirror that into
  // the link's aria-label using the one/other templates on the link.
  const watchCartLabel = (root, signal) => {
    const link = root.querySelector('[data-cart-link]');
    const badge = link?.querySelector('[data-cart-count]');
    if (!badge) return;
    const plural = new Intl.PluralRules(document.documentElement.lang || 'en');
    const update = () => {
      const count = parseInt(badge.textContent, 10) || 0;
      const template = plural.select(count) === 'one' ? link.dataset.labelOne : link.dataset.labelOther;
      if (template) link.setAttribute('aria-label', template.replace('[count]', count));
    };
    const observer = new MutationObserver(update);
    observer.observe(badge, { childList: true, characterData: true, subtree: true });
    signal.addEventListener('abort', () => observer.disconnect());
  };

  class HeaderRoot extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const { signal } = this.controller;
      const nav = this.querySelector('.header__nav');
      const search = this.querySelector('#SearchDialog');
      if (nav) new DesktopNav(nav, signal);
      new MobileMenu(this, signal);
      if (search) new PredictiveSearch(search, signal);
      watchCartLabel(this, signal);
    }

    disconnectedCallback() {
      this.controller?.abort();
    }
  }

  if (!customElements.get('header-root')) customElements.define('header-root', HeaderRoot);
})();
